import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Loader2, Plus, Trash2, Layers, ArrowRight, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StatsSummaryBar from '../../components/StatsSummaryBar';
import ReferencesSection from '../../components/ReferencesSection';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const AFFIX_TYPES = [
  { value: 'prefix', label: 'Prefix', description: 'Added before root' },
  { value: 'suffix', label: 'Suffix', description: 'Added after root' },
  { value: 'infix', label: 'Infix', description: 'Inserted within root' },
  { value: 'circumfix', label: 'Circumfix', description: 'Wraps around root' },
];

const INFLECTION_CATEGORIES = [
  { value: 'verb_conjugation', label: 'Verb Conjugation' },
  { value: 'noun_declension', label: 'Noun Declension' },
  { value: 'adjective_agreement', label: 'Adjective Agreement' },
  { value: 'pronoun_forms', label: 'Pronoun Forms' },
  { value: 'number_marking', label: 'Number Marking' },
  { value: 'case_marking', label: 'Case Marking' },
  { value: 'tense_aspect', label: 'Tense/Aspect' },
  { value: 'other', label: 'Other' },
];

const PARTS_OF_SPEECH = [
  'noun', 'verb', 'adjective', 'adverb', 'pronoun', 
  'preposition', 'conjunction', 'interjection', 'determiner', 'particle', 'any'
];

const MorphologyPage = () => {
  const { conlang, fetchConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  
  const [activeTab, setActiveTab] = useState('affixes');
  const [affixes, setAffixes] = useState([]);
  const [inflectionRules, setInflectionRules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Affix dialog
  const [affixDialogOpen, setAffixDialogOpen] = useState(false);
  const [affixForm, setAffixForm] = useState({
    affix: '',
    type: 'suffix',
    meaning: '',
    part_of_speech_from: '',
    part_of_speech_to: '',
    examples: '',
    notes: ''
  });
  const [affixLoading, setAffixLoading] = useState(false);
  
  // Inflection rule dialog
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    category: 'verb_conjugation',
    applies_to: 'verb',
    pattern: '',
    forms: '',
    examples: '',
    notes: ''
  });
  const [ruleLoading, setRuleLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [conlangId]);

  const fetchData = async () => {
    try {
      const [affixRes, rulesRes] = await Promise.all([
        axios.get(`${API}/conlangs/${conlangId}/affixes`, { withCredentials: true }),
        axios.get(`${API}/conlangs/${conlangId}/inflection-rules`, { withCredentials: true })
      ]);
      setAffixes(affixRes.data);
      setInflectionRules(rulesRes.data);
    } catch (error) {
      toast.error('Failed to load morphology data');
    } finally {
      setLoading(false);
    }
  };

  const resetAffixForm = () => {
    setAffixForm({
      affix: '',
      type: 'suffix',
      meaning: '',
      part_of_speech_from: '',
      part_of_speech_to: '',
      examples: '',
      notes: ''
    });
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      category: 'verb_conjugation',
      applies_to: 'verb',
      pattern: '',
      forms: '',
      examples: '',
      notes: ''
    });
  };

  const handleAddAffix = async (e) => {
    e.preventDefault();
    setAffixLoading(true);
    
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/affixes`, {
        affix: affixForm.affix,
        type: affixForm.type,
        meaning: affixForm.meaning,
        part_of_speech_from: affixForm.part_of_speech_from || null,
        part_of_speech_to: affixForm.part_of_speech_to || null,
        examples: affixForm.examples ? affixForm.examples.split('\n').filter(s => s.trim()) : [],
        notes: affixForm.notes || null
      }, { withCredentials: true });
      
      setAffixes([...affixes, response.data]);
      setAffixDialogOpen(false);
      resetAffixForm();
      toast.success('Affix added');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add affix');
    } finally {
      setAffixLoading(false);
    }
  };

  const handleDeleteAffix = async (affixId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/affixes/${affixId}`, {
        withCredentials: true
      });
      setAffixes(affixes.filter(a => a.affix_id !== affixId));
      toast.success('Affix deleted');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to delete affix');
    }
  };

  const handleAddRule = async (e) => {
    e.preventDefault();
    setRuleLoading(true);
    
    try {
      // Parse forms from text (key: value format, one per line)
      const formsObj = {};
      if (ruleForm.forms) {
        ruleForm.forms.split('\n').forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) {
            formsObj[key] = value;
          }
        });
      }
      
      const response = await axios.post(`${API}/conlangs/${conlangId}/inflection-rules`, {
        name: ruleForm.name,
        category: ruleForm.category,
        applies_to: ruleForm.applies_to,
        pattern: ruleForm.pattern,
        forms: formsObj,
        examples: ruleForm.examples ? ruleForm.examples.split('\n').filter(s => s.trim()) : [],
        notes: ruleForm.notes || null
      }, { withCredentials: true });
      
      setInflectionRules([...inflectionRules, response.data]);
      setRuleDialogOpen(false);
      resetRuleForm();
      toast.success('Inflection rule added');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add rule');
    } finally {
      setRuleLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/inflection-rules/${ruleId}`, {
        withCredentials: true
      });
      setInflectionRules(inflectionRules.filter(r => r.rule_id !== ruleId));
      toast.success('Rule deleted');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const getAffixTypeColor = (type) => {
    switch (type) {
      case 'prefix': return 'text-secondary border-secondary/50';
      case 'suffix': return 'text-primary border-primary/50';
      case 'infix': return 'text-tertiary border-tertiary/50';
      case 'circumfix': return 'text-quaternary border-quaternary/50';
      default: return 'text-foreground-muted border-border';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      {/* Stats Summary Bar */}
      <StatsSummaryBar conlang={conlang} conlangId={conlangId} currentSection="morphology" />

      {/* Header */}
      <div>
        <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="morphology-title">
          Morphology
        </h2>
        <p className="text-foreground-muted mt-1">
          Define word formation rules for {conlang?.name}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-background-paper border border-border">
          <TabsTrigger value="affixes" data-testid="tab-affixes">
            Affixes ({affixes.length})
          </TabsTrigger>
          <TabsTrigger value="inflection" data-testid="tab-inflection">
            Inflection Rules ({inflectionRules.length})
          </TabsTrigger>
        </TabsList>

        {/* Affixes Tab */}
        <TabsContent value="affixes" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-foreground-muted text-sm">
              Define prefixes, suffixes, infixes, and circumfixes
            </p>
            <Button 
              onClick={() => setAffixDialogOpen(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-affix-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Affix
            </Button>
          </div>

          {affixes.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4" data-testid="affixes-list">
              {affixes.map(affix => (
                <Card key={affix.affix_id} className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-conlang text-xl text-foreground">
                            {affix.type === 'prefix' && `${affix.affix}-`}
                            {affix.type === 'suffix' && `-${affix.affix}`}
                            {affix.type === 'infix' && `-${affix.affix}-`}
                            {affix.type === 'circumfix' && affix.affix}
                          </span>
                          <Badge variant="outline" className={getAffixTypeColor(affix.type)}>
                            {affix.type}
                          </Badge>
                        </div>
                        <p className="text-foreground-muted text-sm mb-2">{affix.meaning}</p>
                        {(affix.part_of_speech_from || affix.part_of_speech_to) && (
                          <div className="flex items-center gap-2 text-xs text-foreground-subtle">
                            {affix.part_of_speech_from && <span>{affix.part_of_speech_from}</span>}
                            {affix.part_of_speech_from && affix.part_of_speech_to && (
                              <ArrowRight className="w-3 h-3" />
                            )}
                            {affix.part_of_speech_to && <span>{affix.part_of_speech_to}</span>}
                          </div>
                        )}
                        {affix.examples?.length > 0 && (
                          <div className="mt-2 text-xs text-foreground-subtle">
                            Examples: {affix.examples.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteAffix(affix.affix_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-background-paper border-border border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  No affixes yet
                </h3>
                <p className="text-foreground-muted mb-4 max-w-md mx-auto">
                  Add prefixes, suffixes, and other affixes to define how words are formed and modified.
                </p>
                <Button onClick={() => setAffixDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Affix
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-primary text-sm">
              <strong>Affixes</strong> modify word meaning or change parts of speech. 
              Prefixes attach before roots (un-happy), suffixes after (-ness), 
              infixes within (Tagalog's -um-), and circumfixes wrap around (German ge-...-t).
            </div>
          </div>
        </TabsContent>

        {/* Inflection Rules Tab */}
        <TabsContent value="inflection" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-foreground-muted text-sm">
              Define conjugation, declension, and agreement patterns
            </p>
            <Button 
              onClick={() => setRuleDialogOpen(true)}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="add-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>

          {inflectionRules.length > 0 ? (
            <div className="space-y-4" data-testid="rules-list">
              {inflectionRules.map(rule => (
                <Card key={rule.rule_id} className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-heading text-lg text-foreground">{rule.name}</span>
                          <Badge variant="outline" className="text-secondary border-secondary/50 text-xs">
                            {rule.category.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="text-foreground-muted border-border text-xs">
                            {rule.applies_to}
                          </Badge>
                        </div>
                        {rule.pattern && (
                          <p className="text-foreground-muted text-sm mb-2">{rule.pattern}</p>
                        )}
                        {Object.keys(rule.forms || {}).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(rule.forms).map(([key, value]) => (
                              <div key={key} className="text-xs bg-accent px-2 py-1 rounded font-conlang">
                                <span className="text-foreground-muted">{key}:</span>{' '}
                                <span className="text-foreground">{value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {rule.examples?.length > 0 && (
                          <div className="mt-2 text-xs text-foreground-subtle">
                            Examples: {rule.examples.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteRule(rule.rule_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-background-paper border-border border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  No inflection rules yet
                </h3>
                <p className="text-foreground-muted mb-4 max-w-md mx-auto">
                  Define how words change form for tense, case, number, person, and other grammatical features.
                </p>
                <Button onClick={() => setRuleDialogOpen(true)} className="bg-secondary hover:bg-secondary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Rule
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
            <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
            <div className="text-secondary text-sm">
              <strong>Inflection rules</strong> describe how words change form based on grammatical context. 
              This includes verb conjugations (I run, she runs), noun declensions (case systems), 
              and agreement patterns (adjective-noun agreement).
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* References Section */}
      <ReferencesSection section="morphology" />

      {/* Add Affix Dialog */}
      <Dialog open={affixDialogOpen} onOpenChange={setAffixDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="affix-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Affix</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Define a new affix for word formation
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAffix} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Affix *</Label>
                <Input
                  value={affixForm.affix}
                  onChange={(e) => setAffixForm({...affixForm, affix: e.target.value})}
                  placeholder="e.g., -tion, un-, -ly"
                  className="bg-background border-border font-mono"
                  required
                  data-testid="affix-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Type *</Label>
                <Select 
                  value={affixForm.type} 
                  onValueChange={(v) => setAffixForm({...affixForm, type: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="affix-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {AFFIX_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Meaning *</Label>
              <Input
                value={affixForm.meaning}
                onChange={(e) => setAffixForm({...affixForm, meaning: e.target.value})}
                placeholder="What this affix adds or changes..."
                className="bg-background border-border"
                required
                data-testid="affix-meaning"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Attaches to (POS)</Label>
                <Select 
                  value={affixForm.part_of_speech_from} 
                  onValueChange={(v) => setAffixForm({...affixForm, part_of_speech_from: v})}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {PARTS_OF_SPEECH.map(pos => (
                      <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Creates (POS)</Label>
                <Select 
                  value={affixForm.part_of_speech_to} 
                  onValueChange={(v) => setAffixForm({...affixForm, part_of_speech_to: v})}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Same" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {PARTS_OF_SPEECH.map(pos => (
                      <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Examples (one per line)</Label>
              <Textarea
                value={affixForm.examples}
                onChange={(e) => setAffixForm({...affixForm, examples: e.target.value})}
                placeholder="root + affix = result"
                className="bg-background border-border min-h-[60px]"
                data-testid="affix-examples"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setAffixDialogOpen(false); resetAffixForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={affixLoading || !affixForm.affix || !affixForm.meaning} className="bg-primary hover:bg-primary/90" data-testid="submit-affix">
                {affixLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Affix'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Inflection Rule Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Inflection Rule</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Define a conjugation, declension, or agreement pattern
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddRule} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Rule Name *</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                placeholder="e.g., Present Tense Conjugation"
                className="bg-background border-border"
                required
                data-testid="rule-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Category</Label>
                <Select 
                  value={ruleForm.category} 
                  onValueChange={(v) => setRuleForm({...ruleForm, category: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="rule-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {INFLECTION_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Applies To</Label>
                <Select 
                  value={ruleForm.applies_to} 
                  onValueChange={(v) => setRuleForm({...ruleForm, applies_to: v})}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="rule-applies">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {PARTS_OF_SPEECH.map(pos => (
                      <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Pattern Description</Label>
              <Textarea
                value={ruleForm.pattern}
                onChange={(e) => setRuleForm({...ruleForm, pattern: e.target.value})}
                placeholder="Describe the rule pattern..."
                className="bg-background border-border min-h-[60px]"
                data-testid="rule-pattern"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Forms (key: value, one per line)</Label>
              <Textarea
                value={ruleForm.forms}
                onChange={(e) => setRuleForm({...ruleForm, forms: e.target.value})}
                placeholder={`1sg: -o
2sg: -as
3sg: -at
1pl: -amos`}
                className="bg-background border-border min-h-[100px] font-mono text-sm"
                data-testid="rule-forms"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Examples (one per line)</Label>
              <Textarea
                value={ruleForm.examples}
                onChange={(e) => setRuleForm({...ruleForm, examples: e.target.value})}
                placeholder="parlar → parlo (I speak)"
                className="bg-background border-border min-h-[60px]"
                data-testid="rule-examples"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setRuleDialogOpen(false); resetRuleForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={ruleLoading || !ruleForm.name} className="bg-secondary hover:bg-secondary/90" data-testid="submit-rule">
                {ruleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MorphologyPage;
